import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-95 dark:ring-offset-background-dark-primary",
  {
    variants: {
      variant: {
        default: "bg-accent text-white hover:bg-accent-hover shadow-sm",
        destructive:
          "bg-error text-white hover:bg-error/90 dark:bg-error dark:text-white dark:hover:bg-error/90",
        outline:
          "border border-border dark:border-border-dark bg-background-secondary dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary hover:bg-background-hover dark:hover:bg-background-dark-hover backdrop-blur-xl backdrop-saturate-150",
        secondary:
          "bg-background-secondary dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary hover:bg-background-hover dark:hover:bg-background-dark-hover border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150",
        ghost: "text-accent hover:bg-accent-light hover:text-accent-hover dark:text-accent dark:hover:bg-accent-light dark:hover:text-accent-hover",
        link: "text-accent underline-offset-4 hover:underline hover:text-accent-hover dark:text-accent dark:hover:text-accent-hover",
        success: "bg-success text-white hover:bg-accent-hover shadow-sm",
        accent: "bg-accent text-white hover:bg-accent-hover shadow-sm",
      },
      size: {
        default: "h-11 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-12 px-8",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }